/**
 * A function to create a log object for the 'searchBoxSearchSubmit' event (evid: 240)
 *
 * Event description: Should be sent after visitor submitted search in SearchBox by clicking 'Enter'
 *
 * @param {searchBoxSearchSubmitParams} params - The parameters for this event
 * @property {boolean} isDemo - True|False
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchBoxSearchSubmitParams>} LogObject<searchBoxSearchSubmitParams>
 */
export function searchBoxSearchSubmit(params) {
    return { evid: 240, src: 99, endpoint: 'wix-search-platform', params: params };
}
/**
 * A function to create a log object for the 'searchBoxSuggestionsRequestStarted' event (evid: 241)
 *
 * Event description: Should be sent after visitor change query in SearchBox
 *
 * @param {searchBoxSuggestionsRequestStartedParams} params - The parameters for this event
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @property {string} target - what does user search for (term, phrase, word)
 * @return {LogObject<searchBoxSuggestionsRequestStartedParams>} LogObject<searchBoxSuggestionsRequestStartedParams>
 */
export function searchBoxSuggestionsRequestStarted(params) {
    return { evid: 241, src: 99, endpoint: 'wix-search-platform', params: params };
}
/**
 * A function to create a log object for the 'searchBoxSuggestionsRequestFinished' event (evid: 242)
 *
 * Event description: Sent every time we show new set of suggestions to the visitor while she's typing into the search box
 *
 * @param {searchBoxSuggestionsRequestFinishedParams} params - The parameters for this event
 * @property {boolean} success - success
 * @property {number} resultCount - result count
 * @property {string} resultsArray - {"all":17, "pages":5, "products":2, "blog":10}
 * @property {string} documentIds - {"public/stores/products": ["id1", "id2", ...],   "public/blog/posts": ["id1", "id2", ...], ...}
 * @property {string} error - error
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @property {string} type - trending/suggestions
 * @property {number} loadingDuration - time between load start and load.finish
 * @return {LogObject<searchBoxSuggestionsRequestFinishedParams>} LogObject<searchBoxSuggestionsRequestFinishedParams>
 */
export function searchBoxSuggestionsRequestFinished(params) {
    return { evid: 242, src: 99, endpoint: 'wix-search-platform', params: params };
}
/**
 * A function to create a log object for the 'searchBoxSuggestionClick' event (evid: 243)
 *
 * Event description: Should be sent after visitor submitted search in SearchBox by clicking 'Enter'
 *
 * @param {searchBoxSuggestionClickParams} params - The parameters for this event
 * @property {string} resultsArray - {"all":17, "pages":5, "products":2, "blog":10}
 * @property {string} pageUrl - url
 * @property {string} resultClicked - the result title the user clicked on
 * @property {string} documentType - pages|products|blog
 * @property {number} searchIndex - index of the clicked item from search result set (search_index starts from 0)
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {string} documentId - Unique id for each vertical's document
 * @property {string} correlationId - correlation_id
 * @property {string} type - trending/suggestions
 * @return {LogObject<searchBoxSuggestionClickParams>} LogObject<searchBoxSuggestionClickParams>
 */
export function searchBoxSuggestionClick(params) {
    return { evid: 243, src: 99, endpoint: 'wix-search-platform', params: params };
}
/**
 * A function to create a log object for the 'searchBoxSuggestionShowAllClick' event (evid: 244)
 *
 * Event description: Should be sent after visitor submitted search in SearchBox by clicking 'Enter'
 *
 * @param {searchBoxSuggestionShowAllClickParams} params - The parameters for this event
 * @property {string} documentType - pages|products|blog | all
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchBoxSuggestionShowAllClickParams>} LogObject<searchBoxSuggestionShowAllClickParams>
 */
export function searchBoxSuggestionShowAllClick(params) {
    return { evid: 244, src: 99, endpoint: 'wix-search-platform', params: params };
}
/**
 * A function to create a log object for the 'searchBoxFocused' event (evid: 245)
 *
 * Event description: A search box was focused
 *
 * @param {searchBoxFocusedParams} params - The parameters for this event
 * @property {boolean} isDemo - Indicates that the event was fired from Editor/Preview (true) or live site (false)
 * @property {boolean} isFullscreen - Indicates whether UoU was shown a fullscreen search box experience in mobile
 * @return {LogObject<searchBoxFocusedParams>} LogObject<searchBoxFocusedParams>
 */
export function searchBoxFocused(params) {
    return { evid: 245, src: 99, endpoint: 'wix-search-platform', params: params };
}
/**
 * A function to create a log object for the 'searchBoxStartedWritingAQuery' event (evid: 246)
 *
 * Event description: Fires when input length changes from 0 to 1.
 *
 * @param {searchBoxStartedWritingAQueryParams} params - The parameters for this event
 * @property {boolean} isDemo - true | false
 * @return {LogObject<searchBoxStartedWritingAQueryParams>} LogObject<searchBoxStartedWritingAQueryParams>
 */
export function searchBoxStartedWritingAQuery(params) {
    return { evid: 246, src: 99, endpoint: 'wix-search-platform', params: params };
}
/**
 * A function to create a log object for the 'searchBoxCleared' event (evid: 247)
 *
 * Event description: Search Box > Cleared.
 *
 * @param {searchBoxClearedParams} params - The parameters for this event
 * @property {string} resultsArray - results_array
 * @property {boolean} isDemo - is_demo
 * @property {string} target - what does user search for (term, phrase, word)true/false
 * @property {string} correlationId - Used to match many different BI events with the same search
 * @return {LogObject<searchBoxClearedParams>} LogObject<searchBoxClearedParams>
 */
export function searchBoxCleared(params) {
    return { evid: 247, src: 99, endpoint: 'wix-search-platform', params: params };
}
/**
 * A function to create a log object for the 'searchBoxAutoCompleteShown' event (evid: 248)
 *
 * Event description: Should be sent after visitor got auto complete in search box
 *
 * @param {searchBoxAutoCompleteShownParams} params - The parameters for this event
 * @property {string} suggestion - what was suggested?
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchBoxAutoCompleteShownParams>} LogObject<searchBoxAutoCompleteShownParams>
 */
export function searchBoxAutoCompleteShown(params) {
    return { evid: 248, src: 99, endpoint: 'wix-search-platform', params: params };
}
/**
 * A function to create a log object for the 'searchBoxAutoCompleteShownSuggestionApproved' event (evid: 249)
 *
 * Event description: Should be sent after visitor got auto complete in search box and approved it
 *
 * @param {searchBoxAutoCompleteShownSuggestionApprovedParams} params - The parameters for this event
 * @property {string} previousTarget - what was before the suggestion approved?
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchBoxAutoCompleteShownSuggestionApprovedParams>} LogObject<searchBoxAutoCompleteShownSuggestionApprovedParams>
 */
export function searchBoxAutoCompleteShownSuggestionApproved(params) {
    return { evid: 249, src: 99, endpoint: 'wix-search-platform', params: params };
}
//# sourceMappingURL=index.js.map